/**
 * BlockTemplatePanel Component
 *
 * Right sidebar panel with the live block catalog (UXR C1/C3):
 * - BLOCK TYPES: the built-in strategy block templates
 * - PROJECT SUBTYPES: enabled project subtypes as first-class cards
 *
 * Features:
 * - One searchable column over both groups
 * - Click «+ Add» (drops at viewport center) and drag-to-position
 * - Header gear opens the Block Subtype manager (the ONLY entry — Q1)
 * - Disabled subtypes stay visible but are not addable
 *
 * Requirements:
 * - FR-P5-005: Template Panel shows blocks when mode is 'Блоки стратегии'
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  TextField,
  List,
  ListItem,
  Tooltip,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import type { StrategyBlock } from '@segmently/schema-models/strategy';
import type { BlockSubtype } from '@modules/firebase-core';
import { assessLibraryExampleRenderability, type BlockLibraryExampleDoc } from '@modules/shared-types';
import { blockPreviewRenderer, BLOCK_TYPE_COLORS } from '@modules/reactFlow/services/block/BlockPreviewRenderer';
import { EXAMPLE_TEMPLATE_ID_PREFIX, SUBTYPE_TEMPLATE_ID_PREFIX } from '@modules/reactFlow/services/block/BlockFlowCoordinator';
import { warningToneBg, warningToneText } from '@modules/strategy-blocks/utils/warningTone';
import { TEMPLATE_PANEL_CONFIG } from '@modules/reactFlow/constants/screenPreviewConfig';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';

/**
 * BlockTemplatePanel Props
 */
export interface BlockTemplatePanelProps {
  /** Array of template definitions (StrategyBlock instances) */
  templates?: StrategyBlock[];
  /** Project subtype catalog (enabled AND disabled — disabled render inert). */
  subtypes?: BlockSubtype[];
  /** Block Examples Library (project tier; imported system examples included). */
  examples?: BlockLibraryExampleDoc[];
  /** Advisory contract-fit summary per example against the CURRENT strategy. */
  fitSummaryByExample?: Record<string, { mapped: number; created: number; needsAdapt: number }>;
  /** Opens the library browser (Phase 2) — absent renders the entry disabled. */
  onBrowseLibrary?: () => void;
  /** Callback when template drag starts */
  onTemplateDragStart?: (template: StrategyBlock) => void;
  /** Callback when template drag ends */
  onTemplateDragEnd?: () => void;
  /** Click-to-add: receives a templateId (`template-block-*` or `subtype::<slug>`). */
  onTemplateAdd?: (templateId: string) => void;
  /** Opens the Block Subtype manager dialog (panel gear / ghost card). */
  onManageSubtypes?: () => void;
  /**
   * Opens the example DETAILS dialog (screens + instructions preview before
   * insert). Rendered on example cards INCLUDING broken/disabled ones — the
   * renderability reason must stay discoverable (FP-7).
   */
  onExampleDetails?: (exampleId: string) => void;
}

interface PanelCard {
  templateId: string;
  key: string;
  title: string;
  description: string;
  accent: string;
  slug?: string;
  disabled?: boolean;
  dragPayload?: StrategyBlock;
  /** Block Examples Library card extras. */
  exampleId?: string;
  originLabel?: 'My' | 'System';
  warnAssets?: boolean;
}

/** Portable envelopes flag assets that must rebuild after insertion. */
function exampleHasAssetPlans(example: BlockLibraryExampleDoc): boolean {
  return example.blueprint.assetExamples.length > 0
    || example.blueprint.screens.some((screen) => (screen.aiMeta?.assetIntents?.length ?? 0) > 0);
}

/**
 * BlockTemplatePanel Component
 *
 * Right sidebar with the searchable block catalog (built-ins ⊕ project subtypes)
 */
export const BlockTemplatePanel: React.FC<BlockTemplatePanelProps> = ({
  templates: externalTemplates,
  subtypes,
  examples,
  fitSummaryByExample,
  onBrowseLibrary,
  onTemplateDragStart,
  onTemplateDragEnd,
  onTemplateAdd,
  onManageSubtypes,
  onExampleDetails,
}) => {
  const theme = useTheme();

  // Get templates from BlockPreviewRenderer if not provided
  const templates = useMemo(
    () => externalTemplates || blockPreviewRenderer.getTemplateBlocks(),
    [externalTemplates]
  );

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  const builtinCards = useMemo<PanelCard[]>(() => templates.map((template) => ({
    templateId: String(template.id),
    key: String(template.blockType ?? template.id),
    title: template.name ?? String(template.blockType),
    description: template.description ?? '',
    accent: BLOCK_TYPE_COLORS[String(template.blockType)] ?? BLOCK_TYPE_COLORS.custom,
    dragPayload: template as StrategyBlock,
  })), [templates]);

  const exampleCards = useMemo<PanelCard[]>(() => (examples ?? []).map((example) => {
    // AC-P1-036: a broken envelope renders the card INERT with the reason —
    // it must never be insertable from the palette.
    const renderability = assessLibraryExampleRenderability(example);
    return {
      templateId: `${EXAMPLE_TEMPLATE_ID_PREFIX}project::${example.id}`,
      key: `example-${example.id}`,
      title: example.name,
      description: renderability.renderable
        ? (example.description
          || `${example.screensCount} screens · ${example.contractSummary.inputs} in / ${example.contractSummary.outputs} out`)
        : `Cannot be inserted: ${renderability.reason}`,
      accent: BLOCK_TYPE_COLORS[example.scopeMetadata.blockType] ?? BLOCK_TYPE_COLORS.custom,
      disabled: !example.enabled || !renderability.renderable,
      exampleId: example.id,
      originLabel: example.systemExampleId ? 'System' as const : 'My' as const,
      warnAssets: renderability.renderable && exampleHasAssetPlans(example),
    };
  }), [examples]);

  const subtypeCards = useMemo<PanelCard[]>(() => (subtypes ?? []).map((subtype) => ({
    templateId: `${SUBTYPE_TEMPLATE_ID_PREFIX}${subtype.slug}`,
    key: subtype.slug,
    title: subtype.name,
    description: subtype.description || 'Project subtype — enable instructions in the manager',
    accent: BLOCK_TYPE_COLORS.custom,
    slug: subtype.slug,
    disabled: !subtype.enabled,
  })), [subtypes]);

  const query = searchQuery.trim().toLowerCase();
  const cardMatchesQuery = (card: PanelCard): boolean => !query
    || card.title.toLowerCase().includes(query)
    || card.key.toLowerCase().includes(query)
    || card.description.toLowerCase().includes(query);

  const filteredBuiltins = builtinCards.filter(cardMatchesQuery);
  const filteredExamples = exampleCards.filter(cardMatchesQuery);
  const filteredSubtypes = subtypeCards.filter(cardMatchesQuery);
  const nothingFound = filteredBuiltins.length === 0
    && filteredExamples.length === 0
    && filteredSubtypes.length === 0;

  // Handle drag start — payload stays {templateId} so the whole existing
  // ReactFlowCanvas → block.created → coordinator chain is reused as-is.
  const handleDragStart = (card: PanelCard) => (event: React.DragEvent) => {
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData('application/json', JSON.stringify({ templateId: card.templateId }));
    if (onTemplateDragStart && card.dragPayload) {
      onTemplateDragStart(card.dragPayload);
    }
  };

  // Handle drag end
  const handleDragEnd = () => {
    if (onTemplateDragEnd) {
      onTemplateDragEnd();
    }
  };

  const renderCard = (card: PanelCard) => (
    <ListItem
      key={card.templateId}
      data-testid={card.exampleId
        ? STRATEGY_CANVAS.templateItemExample(card.exampleId)
        : card.slug
          ? STRATEGY_CANVAS.templateItemSubtype(card.slug)
          : `block-template-item-${card.key}`}
      data-template-id={card.templateId}
      draggable={!card.disabled}
      onDragStart={card.disabled ? undefined : handleDragStart(card)}
      onDragEnd={card.disabled ? undefined : handleDragEnd}
      sx={{
        padding: 0,
        marginBottom: `${TEMPLATE_PANEL_CONFIG.ITEM_SPACING}px`,
        cursor: card.disabled ? 'not-allowed' : 'grab',
        opacity: card.disabled ? 0.55 : 1,
        '&:active': { cursor: card.disabled ? 'not-allowed' : 'grabbing' },
      }}
    >
      <Box
        sx={{
          width: '100%',
          border: `1px solid ${theme.palette.divider}`,
          borderLeft: `4px solid ${card.accent}`,
          borderRadius: '8px',
          padding: 1.25,
          background: theme.palette.background.paper,
          position: 'relative',
          '&:hover': { background: card.disabled ? undefined : theme.palette.action.hover },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pr: card.disabled ? 0 : 7, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontSize: '12.5px', fontWeight: 600 }}>
            {card.title}
          </Typography>
          {card.slug && !card.disabled && (
            <Chip
              size="small"
              label={card.slug}
              data-testid={STRATEGY_CANVAS.templateSubtypeBadge(card.slug)}
              sx={{ height: 16, fontSize: '9px', fontFamily: 'monospace' }}
            />
          )}
          {card.slug && card.disabled && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="disabled"
              data-testid={STRATEGY_CANVAS.templateDisabledBadge(card.slug)}
              sx={{ height: 16, fontSize: '9px' }}
            />
          )}
          {card.exampleId && card.originLabel && (
            <Chip
              size="small"
              variant="outlined"
              label={card.originLabel}
              data-testid={STRATEGY_CANVAS.templateExampleOriginBadge(card.exampleId)}
              sx={{ height: 16, fontSize: '9px' }}
            />
          )}
          {card.exampleId && card.warnAssets && (
            <Tooltip title="Generated assets rebuild after inserting this example">
              <Chip
                size="small"
                variant="outlined"
                label="assets rebuild"
                data-testid={STRATEGY_CANVAS.templateExampleWarnBadge(card.exampleId)}
                sx={{
                  height: 16,
                  fontSize: '9px',
                  // AA pair, not raw warning.main (3.11:1 at 9px) — invariant #8.
                  color: warningToneText,
                  borderColor: 'warning.main',
                  bgcolor: warningToneBg,
                }}
              />
            </Tooltip>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35, mt: 0.25 }}>
          {card.description}
        </Typography>
        {card.exampleId && fitSummaryByExample?.[card.exampleId] && (() => {
          const fit = fitSummaryByExample[card.exampleId!];
          const parts: string[] = [];
          if (fit.mapped > 0) parts.push(`${fit.mapped} mapped`);
          if (fit.created > 0) parts.push(`${fit.created} new`);
          return (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.25,
                fontSize: '10px',
                color: fit.needsAdapt > 0 ? warningToneText : 'text.secondary',
              }}
            >
              {`fit: ${parts.length > 0 ? parts.join(' · ') : 'no contract'}`}
              {fit.needsAdapt > 0 ? ` · ${fit.needsAdapt} needs adapt` : ''}
            </Typography>
          );
        })()}
        {!card.disabled && onTemplateAdd && (
          <Button
            size="small"
            variant="outlined"
            data-testid={STRATEGY_CANVAS.templateAddButton(card.key)}
            onClick={() => onTemplateAdd(card.templateId)}
            sx={{
              position: 'absolute',
              top: 6,
              right: 8,
              minWidth: 'auto',
              px: 0.75,
              py: 0,
              fontSize: '11px',
              textTransform: 'none',
            }}
          >
            + Add
          </Button>
        )}
        {/* Details preview — also on broken/disabled cards (the renderability
            reason must stay discoverable, FP-7). */}
        {card.exampleId && onExampleDetails && (
          <IconButton
            size="small"
            aria-label={`Details of ${card.title}`}
            data-testid={STRATEGY_CANVAS.templateExampleDetails(card.exampleId)}
            onClick={(event) => {
              event.stopPropagation();
              onExampleDetails(card.exampleId!);
            }}
            sx={{
              position: 'absolute',
              top: 4,
              right: card.disabled || !onTemplateAdd ? 8 : 56,
              p: 0.5,
              opacity: 1,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}
      </Box>
    </ListItem>
  );

  const groupLabel = (label: string, count?: number) => (
    <Typography
      variant="overline"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '10px',
        letterSpacing: '0.8px',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        lineHeight: 2,
        mt: 0.5,
      }}
    >
      {label}
      {count !== undefined && <Chip size="small" label={count} sx={{ height: 16, fontSize: '10px' }} />}
    </Typography>
  );

  return (
    <Box
      data-testid="block-template-panel"
      role="complementary"
      aria-label="Block Template Panel"
      sx={{
        width: TEMPLATE_PANEL_CONFIG.PANEL_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.background.paper,
        borderLeft: `2px solid ${theme.palette.divider}`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 48,
          padding: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
          Block Templates
        </Typography>
        <Tooltip title="Manage block subtypes">
          <IconButton
            size="small"
            aria-label="Manage block subtypes"
            onClick={onManageSubtypes}
            data-testid={STRATEGY_CANVAS.templateSettings}
            sx={{ position: 'absolute', right: 16 }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search Field */}
      <Box sx={{ padding: 2, paddingBottom: 1 }}>
        <TextField
          data-testid="block-template-search"
          fullWidth
          size="small"
          placeholder="Search block templates"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          inputProps={{
            'aria-label': 'Search block templates',
          }}
        />
      </Box>

      {/* Templates List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: 2,
          paddingTop: 0.5,
        }}
      >
        {nothingFound ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 4,
              textAlign: 'center',
            }}
          >
            <Typography data-testid="block-template-no-results" variant="body2" color="text.secondary">
              No results found
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Try different keywords
            </Typography>
          </Box>
        ) : (
          <List data-testid="block-template-list" sx={{ padding: 0 }}>
            {filteredBuiltins.length > 0 && groupLabel('Block types')}
            {filteredBuiltins.map(renderCard)}
            {/* Block Examples Library — confirmed order: TYPES → EXAMPLES → SUBTYPES */}
            {(filteredExamples.length > 0 || (!query && examples !== undefined)) && (
              groupLabel('Examples', exampleCards.length || undefined)
            )}
            {filteredExamples.map(renderCard)}
            {!query && examples !== undefined && exampleCards.length === 0 && (
              <ListItem sx={{ padding: 0, mb: 0.5 }}>
                <Box
                  data-testid={STRATEGY_CANVAS.templateExamplesEmptyCta}
                  sx={{
                    width: '100%',
                    border: `1.5px dashed ${theme.palette.divider}`,
                    borderRadius: '8px',
                    p: 1.25,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    No saved examples yet
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    Use ⋮ → «Save as library blueprint…» on any block to reuse it here.
                  </Typography>
                </Box>
              </ListItem>
            )}
            {!query && examples !== undefined && (
              <ListItem sx={{ padding: 0 }}>
                <Tooltip title={onBrowseLibrary ? '' : 'System library comes next'}>
                  <span style={{ width: '100%' }}>
                    <Button
                      fullWidth
                      size="small"
                      disabled={!onBrowseLibrary}
                      onClick={onBrowseLibrary}
                      data-testid={STRATEGY_CANVAS.templateBrowseLibrary}
                      sx={{
                        border: `1.5px dashed ${theme.palette.divider}`,
                        borderRadius: '8px',
                        textTransform: 'none',
                        color: theme.palette.text.secondary,
                        py: 0.75,
                        mb: 0.5,
                      }}
                    >
                      Browse library…
                    </Button>
                  </span>
                </Tooltip>
              </ListItem>
            )}
            {(filteredSubtypes.length > 0 || (!query && onManageSubtypes)) && (
              groupLabel('Project subtypes', subtypeCards.length || undefined)
            )}
            {filteredSubtypes.map(renderCard)}
            {!query && onManageSubtypes && (
              <ListItem sx={{ padding: 0 }}>
                <Button
                  fullWidth
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={onManageSubtypes}
                  data-testid={STRATEGY_CANVAS.templateNewSubtype}
                  sx={{
                    border: `1.5px dashed ${theme.palette.divider}`,
                    borderRadius: '8px',
                    textTransform: 'none',
                    color: theme.palette.text.secondary,
                    py: 1,
                  }}
                >
                  New subtype…
                </Button>
              </ListItem>
            )}
          </List>
        )}
      </Box>

      {/* Interaction hint */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: 'center', px: 1.5, py: 1, borderTop: `1px solid ${theme.palette.divider}` }}
      >
        Click «+ Add» to drop at viewport center · drag to position
      </Typography>
    </Box>
  );
};

export default BlockTemplatePanel;
